package com.downdogapp.client.widget

import com.downdogapp.*
import com.downdogapp.BoxSizing.BORDER_BOX
import com.downdogapp.Color.Companion.BLACK
import com.downdogapp.Color.Companion.WHITE
import com.downdogapp.FontWeight.BOLD
import com.downdogapp.FontWeight.MEDIUM
import com.downdogapp.Position.ABSOLUTE
import com.downdogapp.Position.RELATIVE
import com.downdogapp.Size.Companion.percent
import com.downdogapp.Size.Companion.px
import com.downdogapp.Size.INHERIT
import com.downdogapp.Size.Pixels
import com.downdogapp.client.AppPage
import com.downdogapp.client.Strings
import com.downdogapp.client.resources.Image
import com.downdogapp.client.resources.Images
import com.downdogapp.client.resources.image
import com.downdogapp.client.singleton.App
import com.downdogapp.client.views.start.StartView.Companion.TITLE_TOP_MARGIN_PX
import kotlinx.browser.window
import org.w3c.dom.*
import kotlin.contracts.ExperimentalContracts
import kotlin.contracts.InvocationKind.EXACTLY_ONCE
import kotlin.contracts.contract
import kotlin.math.pow

var HTMLElement.text: String?
  get() = textContent
  set(value) {
    textContent = value
  }

fun HTMLElement.separator(color: Color = Color.grey(0.4)) =
  div {
    width = percent(100)
    height = px(1)
    bgColor = color
  }

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.imageButton(image: Image,
                                   crossinline clickFn: () -> Unit,
                                   block: HTMLImageElement.() -> Unit = {}): HTMLImageElement {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return imageButtonInternal(image, image.width, image.height, clickFn, block)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.imageButton(width: Int,
                                   height: Int,
                                   crossinline clickFn: () -> Unit,
                                   block: HTMLImageElement.() -> Unit = {}): HTMLImageElement {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return imageButtonInternal(null, width, height, clickFn, block)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.imageButtonInternal(image: Image?,
                                           width: Int,
                                           height: Int,
                                           crossinline clickFn: () -> Unit,
                                           block: HTMLImageElement.() -> Unit = {}): HTMLImageElement {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return img {
    this.image = image
    this.width = width
    this.height = height
    setAttribute("role", "button")

    onClick {
      clickFn()
    }

    block()
  }
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.imageView(image: Image,
                                 block: HTMLImageElement.() -> Unit = {}): HTMLImageElement {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return imageViewInternal(image, image.width, image.height, block)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.imageView(width: Int,
                                 height: Int,
                                 block: HTMLImageElement.() -> Unit = {}): HTMLImageElement {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return imageViewInternal(null, width, height, block)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.imageViewInternal(image: Image?,
                                         width: Int,
                                         height: Int,
                                         block: HTMLImageElement.() -> Unit = {}): HTMLImageElement {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return img {
    this.image = image
    this.width = width
    this.height = height
    cursor = Cursor.INHERIT

    block()
  }
}

fun createLabel(fontSize: Int,
                fontWeight: FontWeight = FontWeight.REGULAR,
                color: Color = WHITE,
                block: HTMLSpanElement.() -> Unit = {}) =
  createSpan {
    txtColor = color
    this.fontSize = px(fontSize)
    this.fontWeight = fontWeight
    fontFamily = FONT_FAMILY
    cursor = Cursor.INHERIT
    block()
  }

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.alertLabel(text: String,
                                  fontWeight: FontWeight = FontWeight.REGULAR,
                                  block: HTMLSpanElement.() -> Unit = {}): HTMLSpanElement {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return label(text = text, fontSize = 20, fontWeight = fontWeight, color = BLACK, block = block)
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.topOffsetDiv(top: Size, block: HTMLDivElement.() -> Unit = {}): HTMLDivElement {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return div {
    position = RELATIVE
    this.top = top
    width = percent(100)

    centerHorizontally(block)
  }
}

fun HTMLDivElement.pageHeader(title: String? = null, titleSize: Int = 20, onBackClicked: (() -> Unit)? = null) =
  div {
    width = percent(100)
    marginTop = TITLE_TOP_MARGIN_PX
    height = px(80)
    position = RELATIVE

    title?.let {
      centerInParent {
        label(title, titleSize, MEDIUM)
      }.apply {
        position = ABSOLUTE
      }
    }

    onBackClicked?.let {
      centerVertically {
        imageButton(Images.SETTINGS_LEFT_ARROW, onBackClicked) {
          marginLeft = HORIZONTAL_CONTENT_GUTTER_PX
        }
      }.apply {
        position = ABSOLUTE
      }
    }
  }

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.textButton(text: String? = null,
                                  fontSize: Int,
                                  fontWeight: FontWeight = MEDIUM,
                                  color: Color = WHITE,
                                  textAlign: TextAlign? = null,
                                  noinline clickFn: (() -> Unit)?,
                                  block: HTMLButtonElement.() -> Unit = {}): HTMLButtonElement {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return button {
    margin = px(0)
    padding = px(0)
    style.background = "none"
    border = Border.NONE
    txtColor = color
    fontFamily = FONT_FAMILY
    this.fontWeight = fontWeight
    this.fontSize = px(fontSize)
    text?.let {
      textContent = it
    }
    textAlign?.let {
      this.textAlign = it
    }
    clickFn?.let {
      onClick {
        clickFn()
      }
    }
    block()
  }
}

fun HTMLElement.roundedTextButton(text: String,
                                  fontSize: Int = 15,
                                  color: Color = WHITE,
                                  bgColor: Color = Color.DOWN_DOG_BLUE,
                                  marginHorizontal: Size = px(0),
                                  height: Pixels = ROUNDED_TEXT_BUTTON_HEIGHT_PX,
                                  clickFn: () -> Unit,
                                  block: HTMLDivElement.() -> Unit = {}) =
  TextButton(this, true, text, color, px(fontSize), clickFn).apply {
    container.apply {
      width = percent(100)
      paddingHorizontal = marginHorizontal
      block()
    }
    button.apply {
      this.height = height
      this.bgColor = bgColor
      borderRadius = height / 2
      fontWeight = BOLD
    }
  }

fun HTMLElement.settingsCancelButton(text: String = Strings.CANCEL, clickFn: () -> Unit) =
  div {
    width = INHERIT
    height = ROUNDED_TEXT_BUTTON_HEIGHT_PX
    centerInParent {
      textButton(text.uppercase(), 15, FontWeight.SEMIBOLD, clickFn = clickFn)
    }
  }

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.customEditText(block: HTMLTextAreaElement.() -> Unit = {}): HTMLTextAreaElement {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return textArea {
    style.resize = "none"
    fontFamily = FONT_FAMILY
    block()
  }
}

@OptIn(ExperimentalContracts::class)
inline fun controllerView(block: HTMLDivElement.() -> Unit = {}): HTMLDivElement {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return createDiv {
    width = INHERIT
    height = INHERIT
    position = ABSOLUTE

    block()
  }
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.background(blur: Boolean = false,
                                  image: Image? = App.getBackgroundImage(),
                                  block: HTMLDivElement.() -> Unit = {}): Background {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return Background(this, blur, image).also {
    it.root.apply(block)
  }
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.foreground(hasSidebar: Boolean = false,
                                  block: HTMLDivElement.() -> Unit = {}): Foreground {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return Foreground(this, hasSidebar).also {
    it.applyContentBlock(block)
  }
}

@OptIn(ExperimentalContracts::class)
inline fun HTMLElement.centeredColumnView(block: HTMLDivElement.() -> Unit): HTMLDivElement {
  contract {
    callsInPlace(block, EXACTLY_ONCE)
  }
  return centerHorizontally {
    div {
      width = CENTERED_COLUMN_WIDTH_PX
      height = percent(100)
      block()
    }
  }
}

fun HTMLDivElement.blurView() {
  val depth = AppPage.getViewControllers().size
  val blurRadius = 5 + depth * 9
  val brightness = 65 * 0.8.pow(depth)

  filter = Filter.blurAndBrightness(px(blurRadius), percent(brightness))
}

fun HTMLElement.fadeOut(duration: Duration = DEFAULT_FADE_TIME, onFinished: () -> Unit = {}) {
  if (getAttribute("fadingOut") != "true" && opacity ?: 1.0 > 0.0) {
    setAttribute("fadingIn", "false")
    setAttribute("fadingOut", "true")
    val startOpacity = opacity ?: 1.0
    Animation.animate(
      animationLength = duration,
      onFinished = {
        if (getAttribute("fadingOut") == "true") {
          hide()
          opacity = 0.0
          setAttribute("fadingOut", "false")
        }
        onFinished()
      }
    ) {
      if (getAttribute("fadingOut") == "true") {
        opacity = startOpacity - it * startOpacity
        true
      } else {
        false
      }
    }
  }
}

fun HTMLElement.fadeIn(duration: Duration = DEFAULT_FADE_TIME, onFinished: () -> Unit = {}) {
  if (getAttribute("fadingIn") != "true") {
    if (opacity ?: 1.0 < 1.0) {
      setAttribute("fadingOut", "false")
      setAttribute("fadingIn", "true")
      show()
      val startOpacity = opacity ?: 1.0
      Animation.animate(
        animationLength = duration,
        onFinished = {
          if (getAttribute("fadingIn") == "true") {
            opacity = 1.0
            setAttribute("fadingIn", "false")
          }
          onFinished()
        }
      ) {
        if (getAttribute("fadingIn") == "true") {
          opacity = startOpacity + (1 - startOpacity) * it
          true
        } else {
          false
        }
      }
    } else {
      show()
    }
  }
}

fun HTMLElement.scrollToCenter() =
  scrollIntoView(js("{ block: 'center', inline: 'center' }"))

// If view doesn't have a parent, no-op
fun HTMLElement.removeFromParentNode() {
  parentNode?.removeChild(this)
}

class TextButton(parent: HTMLElement,
                 private val uppercased: Boolean = false,
                 text: String?,
                 txtColor: Color = WHITE,
                 fontSize: Size,
                 clickFn: () -> Unit) {

  var button: HTMLButtonElement
  val container = parent.div {
    boxSizing = BORDER_BOX

    button = button {
      width = percent(100)
      border = Border.NONE
      this.fontSize = fontSize
      this.txtColor = txtColor
      fontFamily = FONT_FAMILY

      onClick {
        clickFn()
      }
    }
  }

  init {
    setText(text)
  }

  fun setText(text: String?) {
    button.text = if (uppercased) text?.uppercase() else text
  }

  fun hide() {
    container.hide()
  }

  fun show() {
    container.show()
  }
}

enum class ScreenWidth {
  PHONE,
  SMALL,
  MEDIUM,
  LARGE;

  companion object {

    fun fromPixels(width: Int): ScreenWidth {
      return when {
        width <= 480 -> PHONE
        width <= 800 -> SMALL
        width <= 1260 -> MEDIUM
        else -> LARGE
      }
    }
  }
}

fun HTMLElement.screenWidthDependent(block: HTMLElement.(width: ScreenWidth) -> Unit) {
  block(ScreenWidth.fromPixels(window.innerWidth))
  var lastWidth = ScreenWidth.fromPixels(window.innerWidth)
  window.addEventListener(
    type = "resize",
    callback = {
      val newWidth = ScreenWidth.fromPixels(window.innerWidth)
      if (newWidth != lastWidth) {
        lastWidth = newWidth
        block(newWidth)
      }
    }
  )
}

val ROUNDED_TEXT_BUTTON_HEIGHT_PX = px(60)
val CENTERED_COLUMN_WIDTH_PX = px(500)
val HORIZONTAL_CONTENT_GUTTER_PX = px(20)

val DEFAULT_FADE_TIME = seconds(0.8)
